import { useTranslation } from 'react-i18next';
import { Popover } from '@/components/Popover';
import { useCategoryOptionColorsResolver } from '@/features/colors/hooks/useCategoryOptionColorsResolver';
import { Box, Button, Flex, type FlexProps, Text, useDisclosure } from '@/ui';
import type { Category } from '../../types';
import { CategoryProvider } from '../CategoryProvider';
import { CategorySelectContextProvider } from '../CategorySelect/CategorySelectContextProvider';
import { CategorySelect } from '../CategorySelect/components/CategorySelect';
import { CategoryIcon } from './CategoryIcon';

type Props = {
  value: Category | null;
  isReadOnly?: boolean;
  // Spacing between the icon and the control. Defaults to "3" to match the
  // EditEventModal fields; inline-card usage passes "2" to align with the
  // other inline fields.
  gap?: FlexProps['gap'];
  onChange: (category: Category | null) => void;
};

export const CategoryInput = ({
  value,
  isReadOnly,
  gap = '3',
  onChange,
}: Props) => {
  const { t } = useTranslation('entryModal');
  const { isOpen, onOpen, onClose } = useDisclosure();
  // Fill from the category's palette slot through the experience's palette
  // and bold/light colorMode.
  const resolveOptionColors = useCategoryOptionColorsResolver();
  const colors = resolveOptionColors(value?.colorName);

  if (isReadOnly) {
    if (!value) {
      return null;
    }

    return (
      <Flex align="center" gap={gap} w="100%">
        <CategoryIcon />
        <Box
          bg={colors.backgroundColor}
          borderRadius="md"
          color={colors.textColor}
          fontSize="md"
          minW="200px"
          px="2"
          py="0.5"
        >
          {value.text || ' '}
        </Box>
      </Flex>
    );
  }

  const trigger = (
    <Flex align="center" gap={gap} w="100%">
      <CategoryIcon />
      <Button
        borderRadius="md"
        fontSize="md"
        fontWeight="normal"
        justifyContent="flex-start"
        px="2"
        size="sm"
        variant="ghost"
        onClick={onOpen}
        {...(value
          ? {
              flex: 'none',
              minW: '200px',
              bg: colors.backgroundColor,
              color: colors.textColor,
              _hover: { bg: colors.hoverBackgroundColor },
            }
          : {
              flex: '1',
              color: 'gray.500',
            })}
      >
        {value ? (
          value.text || ' '
        ) : (
          <Text color="gray.500">{t('category.placeholder')}</Text>
        )}
      </Button>
    </Flex>
  );

  return (
    <CategoryProvider>
      <CategorySelectContextProvider>
        {({ goBack }) => (
          <Popover
            isOpen={isOpen}
            trigger={trigger}
            onClose={() => {
              onClose();
              goBack();
            }}
          >
            <CategorySelect
              selected={value}
              onClose={onClose}
              onSelect={onChange}
            />
          </Popover>
        )}
      </CategorySelectContextProvider>
    </CategoryProvider>
  );
};
